import {
  Badge,
  cn,
  createDataTableColumnHelper,
  DataTable,
  Link as UiLink,
  SearchField,
} from "@beweegbaas/ui";
import {
  createFileRoute,
  Outlet,
  stripSearchParams,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useMemo } from "react";
import { DataTableActions, type DataTableAction } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { FilterBar, type FilterBarFilter } from "#/components/filter-bar";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { TicketStatusBadge } from "#/modules/tickets/components/ticket-status-badge";
import { formatDateTime } from "#/modules/util.helpers/format";
import {
  TICKETS_ADMIN_SEARCH_DEFAULTS,
  ticketsAdminSearchSchema,
} from "#/modules/tickets/schema.search.admin";
import {
  closeTicketAdmin,
  deleteTicketAdmin,
  getAdminTicketsOverview,
  reopenTicketAdmin,
} from "#/modules/tickets/tickets-admin.functions";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/tickets")({
  validateSearch: ticketsAdminSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(TICKETS_ADMIN_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    searchTerm: search.searchTerm,
    orderBy: search.orderBy,
    orderDirection: search.orderDirection,
    page: search.page,
    pageSize: search.pageSize,
    status: search.status,
  }),
  loader: ({ deps }) => getAdminTicketsOverview({ data: deps }),
  component: TicketsAdminOverview,
  errorComponent: TicketsError,
});

function TicketsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type AdminTicket = Awaited<ReturnType<typeof getAdminTicketsOverview>>["tickets"][number];

const helper = createDataTableColumnHelper<AdminTicket>();

/** Close/reopen and delete, reusing the drawer's server fns; assigning lives in the drawer's dropdown. */
function TicketRowActions({ ticket }: { ticket: AdminTicket }) {
  const router = useRouter();
  const navigate = useNavigate();
  const closeTicket = useServerFn(closeTicketAdmin);
  const reopenTicket = useServerFn(reopenTicketAdmin);
  const deleteTicket = useServerFn(deleteTicketAdmin);

  const actions: DataTableAction[] = [
    {
      key: "close-reopen",
      label:
        ticket.status === "Open"
          ? m["app.actions.close"]()
          : m["admin.tickets.detail.reopen_button"](),
      icon:
        ticket.status === "Open" ? (
          <IconMaterialSymbolsLockOutline className="size-4" />
        ) : (
          <IconMaterialSymbolsLockOpenOutline className="size-4" />
        ),
      onClick: async () => {
        if (ticket.status === "Open") {
          await closeTicket({ data: { id: ticket.id } });
        } else {
          await reopenTicket({ data: { id: ticket.id } });
        }
        await router.invalidate();
      },
    },
    {
      key: "delete",
      label: m["common.actions.remove"](),
      icon: <IconMaterialSymbolsDeleteOutline className="size-4" />,
      destructive: true,
      confirm: {
        title: m["admin.tickets.detail.delete_confirm_title"](),
        message: m["admin.tickets.detail.delete_confirm_message"](),
      },
      onClick: async () => {
        await deleteTicket({ data: { id: ticket.id } });
        // If this ticket's drawer happens to be open, close it before invalidating — its detail
        // loader would refetch the deleted ticket into TicketNotFound.
        await navigate({
          from: Route.fullPath,
          to: "/$locale/{-$schoolyear}/admin/tickets",
          params: (prev) => prev,
          search: (prev) => prev,
        });
        await router.invalidate();
      },
    },
  ];

  return <DataTableActions actions={actions} aria-label={m["admin.tickets.detail.actions"]()} />;
}

function TicketsAdminOverview() {
  const { tickets, totalPages, totalCount } = Route.useLoaderData();
  const { locale, schoolyear } = Route.useParams();
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, Route.useNavigate());

  const filterBarFilters: FilterBarFilter[] = [
    {
      key: "status",
      label: m["common.fields.status"](),
      options: [
        { value: "Open", label: m["tickets.status_open"]() },
        { value: "Closed", label: m["tickets.status_closed"]() },
      ],
      selectedValues: search.status,
      onValuesChange: (values) => overview.setFilter({ status: values }),
    },
  ];

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("ticketNumber", {
          header: () => m["common.labels.ticket"](),
          enableSorting: true,
          meta: { rowTarget: true },
          cell: ({ row }) => (
            <UiLink
              variant="secondary"
              underline
              to="/$locale/{-$schoolyear}/admin/tickets/drawer/$id"
              params={(prev: Record<string, string>) => ({ ...prev, id: row.original.id })}
              search={search}
              // No hover-preload: the detail loader pulls the full message thread.
              preload={false}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap",
                row.original.hasUnread && "font-bold",
              )}
            >
              {row.original.hasUnread && (
                <span
                  data-testid="admin-ticket-unread-dot"
                  className="size-2 shrink-0 rounded-full bg-linear-to-br from-red-600 to-red-800"
                />
              )}
              #{row.original.ticketNumber}
            </UiLink>
          ),
        }),
        helper.accessor("subject", {
          header: () => m["common.fields.onderwerp"](),
          enableSorting: true,
          cell: ({ getValue, row }) => (
            <span
              className={cn("block max-w-[40ch] truncate", row.original.hasUnread && "font-bold")}
            >
              {getValue()}
            </span>
          ),
        }),
        helper.accessor("status", {
          header: () => m["common.fields.status"](),
          enableSorting: true,
          cell: ({ getValue, row }) =>
            // The ball is in our court: open and the user spoke last. Replaces the plain "Open"
            // badge; unlike the unread dot this survives reading the ticket — only an actual
            // reply (or closing) clears it.
            row.original.awaitingAdminReply ? (
              <Badge variant="orange" size="sm" data-testid="admin-ticket-awaiting-reply">
                {m["admin.tickets.table.awaiting_reply"]()}
              </Badge>
            ) : (
              <TicketStatusBadge status={getValue()} />
            ),
        }),
        helper.accessor("createdByName", {
          header: () => m["common.fields.name"](),
          enableSorting: true,
        }),
        helper.accessor("createdByEmail", {
          header: () => m["admin.users.table.email"](),
          enableSorting: true,
        }),
        helper.accessor("assignedAdminName", {
          header: () => m["admin.tickets.table.assigned_to"](),
          enableSorting: true,
          cell: ({ getValue }) => {
            const name = getValue();
            // Unassigned is the signal that nobody picked the ticket up yet — call it out
            // rather than rendering an empty cell that reads like missing data.
            return name ? (
              <span className="whitespace-nowrap">{name}</span>
            ) : (
              <span className="text-muted-foreground whitespace-nowrap italic">
                {m["admin.tickets.unassigned"]()}
              </span>
            );
          },
        }),
        helper.accessor("lastActivityAt", {
          header: () => m["common.labels.laatste_activiteit"](),
          enableSorting: true,
          cell: ({ getValue }) => formatDateTime(getValue()),
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => <TicketRowActions ticket={row.original} />,
        }),
      ]),
    [locale, schoolyear, search],
  );

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField className="w-auto grow md:max-w-xs" {...overview.searchFieldProps} />
        </Page.Header.SearchField>
        <Page.Header.Section>
          <FilterBar
            filters={filterBarFilters}
            onClearAll={() => overview.setFilter({ status: [] })}
          />
        </Page.Header.Section>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label={m["admin.tickets.title"]()}
          columns={columns}
          data={tickets}
          getRowId={(ticket) => ticket.id}
          empty={m["admin.tickets.table.empty"]()}
          {...overview.sortProps}
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className="mb-6 border-0"
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
      <Outlet />
    </Page>
  );
}
